import type { IAlgoliaProductHit } from "../types/api/algolia";
import type { IProductMetafields } from "../types/api/product";
import type { Metafield, Product, Image as ShopifyImage } from "../types/generated/storefront.types";
import { CurrencyCode } from "../types/generated/storefront.types";
import { ALGOLIA_CONFIG } from "./config";

/**
 * Algolia 레코드 → Shopify Storefront 응답 형태 변환.
 *
 * 화면(ProductCard / Filter / Search / Collections)을 그대로 두고 데이터만 갈아끼우기 위해,
 * Algolia 응답을 기존 Shopify GraphQL 응답과 같은 모양으로 맞춰 돌려준다.
 * 스토어별 메타필드 차이는 config.ts 의 ALGOLIA_META 로 흡수한다.
 */

function metafield(
  { namespace, key }: { namespace: string; key: string },
  value: unknown,
  type = "single_line_text_field",
): Metafield | undefined {
  if (value === undefined || value === null || value === "") return undefined;

  return {
    namespace,
    key,
    type,
    value: typeof value === "string" ? value : JSON.stringify(value),
  } as Metafield;
}

function readMeta(hit: IAlgoliaProductHit, { namespace, key }: { namespace: string; key: string }): unknown {
  return hit.meta?.[namespace]?.[key];
}

/** 공식 앱이 날짜를 unix timestamp(초)로 색인하는 경우가 있어 Date 로 파싱 가능한 문자열로 바꾼다. */
function toISOString(value: number | string | undefined): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "number") return new Date(value * 1000).toISOString();

  const parsed = Number(value);
  if (!Number.isNaN(parsed) && value.trim() !== "") return new Date(parsed * 1000).toISOString();

  return value;
}

/** Shopify 숫자 id → Storefront gid. 이미 gid 면 그대로 둔다. */
function toGid(id: number | string | undefined, type: "Product" | "ProductVariant"): string {
  const raw = String(id ?? "");
  if (raw.startsWith("gid://")) return raw;
  return `gid://shopify/${type}/${raw}`;
}

/**
 * Algolia hit → ProductCard 가 기대하는 Shopify Product 노드.
 *
 * `draw` 는 metaobject reference 라 색인되지 않는다. Draw 카운트다운은 metaobject 를 참조하므로
 * 인덱스에 drop 정보가 없으면 dropDate / publishedAt 기반 표기로 폴백한다.
 */
export function hitToProductNode(hit: IAlgoliaProductHit): Product & IProductMetafields {
  /**
   * 상품 카드의 재고.
   *
   * 인덱스가 변형 단위라 inventory_available 은 "이 변형 하나"의 값이다. 인덱스에 distinct 가 걸려
   * 상품당 한 건만 오면 그 한 건이 품절 사이즈일 때 상품 전체가 품절로 보이므로,
   * 상품 총 재고(variants_inventory_count)를 우선 본다.
   * 재고가 0이어도 오버셀 허용이면 팔 수 있으므로 변형 값도 함께 본다.
   * 이 필드가 없는 인덱스(= 변형을 전부 받아 화면에서 묶는 스토어)에서는 기존처럼 변형 값을 쓴다.
   */
  const availableForSale = (hit.variants_inventory_count ?? 0) > 0 || (hit.inventory_available ?? true);
  const imageUrl = hit.image ?? hit.product_image ?? "";

  // 인덱스가 변형 단위면 hit.price 는 "그 변형"의 값이다.
  // 카드에 보여 줄 값은 상품 단위 최저가(variants_min_price)를 우선한다.
  const price = hit.variants_min_price ?? hit.price;
  const compareAtPrice = hit.variants_compare_at_price_min ?? hit.compare_at_price;

  return {
    id: toGid(hit.id ?? hit.objectID, "Product"),
    handle: hit.handle,
    title: hit.title,
    vendor: hit.vendor,
    productType: hit.product_type ?? "",
    tags: hit.tags ?? [],
    availableForSale,
    publishedAt: toISOString(hit.published_at),
    createdAt: toISOString(hit.created_at),
    featuredImage: imageUrl
      ? ({ url: imageUrl, altText: hit.title, id: `${hit.objectID}-featured`, ...ALGOLIA_CONFIG.imageSize } as ShopifyImage)
      : undefined,

    // 기존 화면은 variants.edges[0] 의 가격을 읽으므로 같은 모양으로 한 건만 만들어 준다.
    variants: {
      edges: [
        {
          node: {
            id: toGid(hit.objectID, "ProductVariant"),
            title: hit.variant_title ?? "",
            sku: hit.sku ?? "",
            availableForSale,
            price: { amount: String(price ?? 0), currencyCode: CurrencyCode.Krw },
            compareAtPrice: compareAtPrice ? { amount: String(compareAtPrice), currencyCode: CurrencyCode.Krw } : null,
            selectedOptions: Object.entries(hit.options ?? {}).map(([name, value]) => ({ name, value })),
          },
        },
      ],
    },

    // 화면 로직이 참조하는 메타필드
    hidden: metafield(ALGOLIA_CONFIG.meta.hidden, String(readMeta(hit, ALGOLIA_CONFIG.meta.hidden) ?? "false")),
    accessible_memberships: metafield(
      ALGOLIA_CONFIG.meta.accessibleMemberships,
      readMeta(hit, ALGOLIA_CONFIG.meta.accessibleMemberships),
      "list.single_line_text_field",
    ),
    dropDate: metafield(ALGOLIA_CONFIG.meta.dropDate, readMeta(hit, ALGOLIA_CONFIG.meta.dropDate), "list.date_time"),
    category: metafield(ALGOLIA_CONFIG.meta.categorySub, readMeta(hit, ALGOLIA_CONFIG.meta.categorySub)),
    color: metafield(ALGOLIA_CONFIG.meta.color, readMeta(hit, ALGOLIA_CONFIG.meta.color)),
    sale: metafield(ALGOLIA_CONFIG.meta.sale, readMeta(hit, ALGOLIA_CONFIG.meta.sale), "boolean"),
    release_date: metafield(ALGOLIA_CONFIG.meta.releaseDate, readMeta(hit, ALGOLIA_CONFIG.meta.releaseDate), "date_time"),
  } as Product & IProductMetafields;
}
